import { Layout } from "@jambushrushti/components/layout/Layout";
import { useEventsFromDB } from "@jambushrushti/hooks/useContent";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  ArrowRight,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

const CommunityEvents = () => {
  const { data: events = [], isLoading } = useEventsFromDB();

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <div className="animate-fade-up mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-primary">{events.length} Events</span>
            </div>
            <h1 className="font-heading animate-fade-up mb-6 text-4xl font-bold text-foreground delay-100 sm:text-5xl">
              Events & Workshops
            </h1>
            <p className="font-body animate-fade-up text-lg text-muted-foreground delay-200">
              Join our lectures, workshops, festivals, and community gatherings
              dedicated to learning and spiritual growth.
            </p>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="bg-background py-16 lg:py-24">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="mx-auto max-w-4xl space-y-6">
              {events.length === 0 ? (
                <div className="py-16 text-center">
                  <Calendar className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                  <h3 className="font-heading mb-2 text-xl font-semibold text-foreground">
                    No events scheduled
                  </h3>
                  <p className="font-body text-muted-foreground">
                    Check back soon for upcoming events!
                  </p>
                </div>
              ) : (
                events.map((event, index) => (
                  <Card
                    key={event.id}
                    variant="feature"
                    className="animate-fade-up overflow-hidden"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex flex-col md:flex-row">
                      {event.image_url && (
                        <div className="md:w-1/3">
                          <img
                            src={event.image_url}
                            alt={event.title}
                            className="h-48 w-full object-cover md:h-full"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <CardHeader>
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                              <Badge variant="default" className="mb-2">
                                Event
                              </Badge>
                              <CardTitle className="text-xl">
                                {event.title}
                              </CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="font-body mb-4 text-base text-muted-foreground">
                            {event.description}
                          </p>
                          <div className="mb-6 grid gap-4 sm:grid-cols-2">
                            <div className="font-body space-y-2 text-base">
                              {event.event_date && (
                                <div className="flex items-center gap-2 text-foreground">
                                  <Calendar className="h-4 w-4 text-primary" />
                                  <span>
                                    {format(
                                      new Date(event.event_date),
                                      "MMMM d, yyyy"
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="font-body space-y-2 text-base">
                              {event.location && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <MapPin className="h-4 w-4" />
                                  <span>{event.location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <Button variant="hero" size="sm" asChild>
                              <Link to={`/community/events/${event.id}`}>
                                Learn More
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* External Link */}
      <section className="bg-secondary/30 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-4 text-xl font-bold text-foreground">
            View More Events
          </h2>
          <p className="mx-auto mb-6 max-w-lg text-muted-foreground">
            Visit the official Siddhi Jambu Parivar website for complete event
            listings and details.
          </p>
          <Button variant="outline" asChild>
            <a
              href="https://siddhijambuparivar.com/events-list/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View All Events
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="bg-gradient-spiritual py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-4 text-2xl font-bold text-foreground">
            Stay Updated
          </h2>
          <p className="mx-auto mb-8 max-w-lg text-muted-foreground">
            Subscribe to our newsletter for updates on upcoming events, research
            publications, and community news.
          </p>
          <Button variant="hero">Subscribe to Newsletter</Button>
        </div>
      </section>
    </Layout>
  );
};

export default CommunityEvents;
